import { defineStore } from 'pinia';
import { ref, watch } from 'vue';

const SENTINEL_COOKIE = 'af-register';

// Le store est persisté en localStorage (partagé entre onglets, sinon le lien
// d'activation reçu par email repart sans contexte d'inscription), et localStorage
// n'a pas d'expiration. La sentinelle en tient lieu : à son expiration, le garde de
// app.ts vide le store au démarrage. Une heure, comme la session d'af/auth — le
// délai court à partir de la saisie de l'email (voir le watch plus bas).
const SENTINEL_TTL_SECONDS = 60 * 60;

export function hasSentinel(): boolean {
  return document.cookie.split(';').some(c => c.trim().startsWith(SENTINEL_COOKIE + '='));
}

function setSentinel() {
  document.cookie = `${SENTINEL_COOKIE}=1; path=/; max-age=${SENTINEL_TTL_SECONDS}; SameSite=Lax`;
}

function clearSentinel() {
  document.cookie = `${SENTINEL_COOKIE}=; path=/; max-age=0; SameSite=Lax`;
}

import type { CompanyBranding } from '@/utils/branding';

export type JobTitle = {
  id?: number;
  label: string;
};

export type RegisterState = {
  email: string;
  firstName: string;
  lastName: string;
  jobTitle: JobTitle | null;
  password: string;
  tou: boolean;
  commercial: boolean;
  // Set after successful register call
  uuid: string;
  // SSO
  sso: boolean;
  providerInfos: {
    provider: string;
    token: string;
    data?: { id: string; email: string; first_name: string; last_name: string } | null;
  } | null;
  // Company branding (set during screening / token lookup)
  branding: CompanyBranding | null;
  // Invite params from URL (token, invited_by_recruiter, sharepool, utm_*, etc.)
  inviteParams: Record<string, string> | null;
};

export const useRegisterStore = defineStore(
  'register',
  () => {
    const email = ref('');
    const firstName = ref('');
    const lastName = ref('');
    const jobTitle = ref<JobTitle | null>(null);
    const password = ref('');
    const tou = ref(false);
    const commercial = ref(false);
    const uuid = ref('');
    const sso = ref(false);
    const providerInfos = ref<{
      provider: string;
      token: string;
      data?: { id: string; email: string; first_name: string; last_name: string } | null;
    } | null>(null);
    const branding = ref<CompanyBranding | null>(null);
    const inviteParams = ref<Record<string, string> | null>(null);

    function setEmail(value: string) {
      email.value = value;
    }

    function setIntroduce(data: { firstName: string; lastName: string; jobTitle: JobTitle | null }) {
      firstName.value = data.firstName;
      lastName.value = data.lastName;
      jobTitle.value = data.jobTitle;
    }

    function setConfirmation(data: { password: string; tou: boolean; commercial: boolean }) {
      password.value = data.password;
      tou.value = data.tou;
      commercial.value = data.commercial;
    }

    function setUuid(value: string) {
      uuid.value = value;
    }

    function setBranding(value: CompanyBranding | null) {
      branding.value = value;
    }

    function setInviteParams(value: Record<string, string> | null) {
      inviteParams.value = value;
    }

    function setSso(value: boolean) {
      sso.value = value;
    }

    function setProviderInfos(
      value: {
        provider: string;
        token: string;
        data?: { id: string; email: string; first_name: string; last_name: string } | null;
      } | null
    ) {
      providerInfos.value = value;
    }

    function reset() {
      email.value = '';
      firstName.value = '';
      lastName.value = '';
      jobTitle.value = null;
      password.value = '';
      tou.value = false;
      commercial.value = false;
      uuid.value = '';
      sso.value = false;
      providerInfos.value = null;
      branding.value = null;
      inviteParams.value = null;
      clearSentinel();
    }

    // Keep sentinel cookie in sync with store data so clearing cookies
    // also invalidates the persisted state — et lui donne son expiration.
    watch(email, val => {
      if (val) setSentinel();
    });

    return {
      email,
      firstName,
      lastName,
      jobTitle,
      password,
      tou,
      commercial,
      uuid,
      sso,
      providerInfos,
      branding,
      inviteParams,
      setEmail,
      setIntroduce,
      setConfirmation,
      setUuid,
      setBranding,
      setInviteParams,
      setSso,
      setProviderInfos,
      reset
    };
  },
  {
    persist: {
      // localStorage et non sessionStorage : ce dernier est cloisonné par onglet,
      // et le lien d'activation reçu par email s'ouvre dans un nouvel onglet — le
      // parcours y perdait branding et invitations. Durée de vie bornée par
      // le cookie sentinelle ci-dessus.
      storage: localStorage,
      // Never persist sensitive credentials
      // password is intentionally excluded — never persisted to disk
      pick: [
        'email',
        'firstName',
        'lastName',
        'jobTitle',
        'tou',
        'commercial',
        'uuid',
        'sso',
        'providerInfos',
        'branding',
        'inviteParams'
      ]
    }
  }
);
